import { CalendarRange, ChevronLeft, ChevronRight, LayoutGrid, List, Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { orgToday } from '@/lib/today';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { addDays, atLeast, formatDuration, isDateOnly, startOfWeek } from '@cfl/shared';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  Combobox,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/cargofl-ui';
import { ConfirmButton } from '@/components/confirm-dialog';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { BulkDialog } from '@/components/timesheet/bulk-dialog';
import { CalendarBanner } from '@/components/timesheet/calendar-banner';
import { EntryDialog } from '@/components/timesheet/entry-dialog';
import { WeekGrid } from '@/components/timesheet/week-grid';
import { WeekStatus } from '@/components/timesheet/week-status';
import { ApiError, toastFailure } from '@/lib/api';
import { useOutstandingWeeks, useSubmitWeek } from '@/lib/approvals-api';
import { useWeekGrid } from '@/lib/grid-api';
import { useVisiblePeople } from '@/lib/people-api';
import { usePeriodShortcuts } from '@/lib/shortcuts';
import type { Session } from '@/lib/session';
import { TagChips } from '@/components/timesheet/tag-chips';
import {
  useDeleteEntry, useWeek, type WeekDay, type WeekEntry,
} from '@/lib/timesheet-api';

type View = 'grid' | 'list';

/**
 * My week: one week, two ways of editing it.
 *
 * These were two routes - a day list and a grid - sitting next to each other in
 * the menu, opening the same week and both writing entries. Neither was a
 * superset of the other: the list holds start time, tags, notes and rates, the
 * grid holds seven numbers per row and nothing else. Only the list could submit
 * the week, and the difference that decided which one to open was invisible
 * until you were already in the wrong one.
 *
 * So the week itself - which week, what it totals, what state it is in, and
 * submitting it - belongs to this frame, and the toggle changes only the surface
 * that edits it. Anything that is a property of the WEEK goes here; anything
 * that is a property of an editing style goes in the view.
 */
export function TimesheetPage({ session }: { session: Session }) {
  // The week and the view both live in the URL: a link can then point at a
  // specific week, which the reminder mail could not do before.
  const [params, setParams] = useSearchParams();

  const requested = params.get('week');
  const anchor =
    requested && isDateOnly(requested)
      ? startOfWeek(requested)
      : startOfWeek(orgToday());

  // Grid is the default: filling a week is the common act, correcting one entry
  // is the rare one.
  const view: View = params.get('view') === 'list' ? 'list' : 'grid';

  const thisWeek = startOfWeek(orgToday());

  const setParam = (next: { week?: string; view?: View }) => {
    const week = next.week ?? anchor;
    const nextView = next.view ?? view;
    const query: Record<string, string> = {};
    // Defaults are left out, so the common URL stays clean and shareable.
    if (week !== thisWeek) query.week = week;
    if (nextView !== 'grid') query.view = nextView;
    // replace, so paging through weeks does not fill the back button with them.
    setParams(query, { replace: true });
  };

  const setAnchor = (next: string) => setParam({ week: next });

  // Left/right or j/k to move a period, t for today. See lib/shortcuts.
  usePeriodShortcuts({
    previous: () => setAnchor(addDays(anchor, -7)),
    next: () => setAnchor(addDays(anchor, 7)),
    today: () => setAnchor(thisWeek),
  });

  // Whose week. A supervisor can open somebody else's to check it before
  // approving; it comes back read-only, because a correction belongs in the
  // owner's own timesheet where every rule already applies to it.
  const canSeeOthers = atLeast(session.role, 'SUPERVISOR');
  const [employeeId, setEmployeeId] = React.useState<string | null>(null);
  const { data: people } = useVisiblePeople(canSeeOthers);
  const viewing = employeeId ?? undefined;

  // Both views are fetched from their own endpoint. Only the active one is
  // enabled, so switching costs one request and staying put costs none.
  const gridQuery = useWeekGrid(anchor, viewing, view === 'grid');
  const listQuery = useWeek(anchor, viewing, view === 'list');

  const submit = useSubmitWeek();
  // Earlier weeks still owed. A warning, not a block: somebody away last week
  // still has to be able to fill this one.
  const { data: outstanding } = useOutstandingWeeks(!viewing);
  const owedBefore = viewing ? [] : (outstanding ?? []).filter((part) => part.end < anchor);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [gridDirty, setGridDirty] = React.useState(false);

  // One dialog for the whole page rather than one per day: seven mounted dialogs
  // each holding their own form state is seven things to reset.
  const [editing, setEditing] = React.useState<{ date: string; entry?: WeekEntry } | null>(null);
  const [bulkOpen, setBulkOpen] = React.useState(false);

  const active = view === 'grid' ? gridQuery : listQuery;
  const week = active.data;

  // Somebody else's week is never submitted from here - it is theirs to submit.
  const readOnly = view === 'grid' ? (gridQuery.data?.readOnly ?? false) : Boolean(viewing);

  return (
    <PageContainer>
      <PageHeader
        title="My week"
        description="Your hours, Monday to Sunday. Fill them in as a grid or day by day."
        actions={
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" aria-label="Previous week" title="Previous week (← or j)"
              onClick={() => setAnchor(addDays(anchor, -7))}>
              <ChevronLeft className="size-4" />
            </Button>
            <Button hint={"Jump to the current week"} variant="outline" onClick={() => setAnchor(thisWeek)}>
              This week
            </Button>
            <Button variant="outline" size="icon" aria-label="Next week" title="Next week (→ or k)"
              onClick={() => setAnchor(addDays(anchor, 7))}>
              <ChevronRight className="size-4" />
            </Button>
            {/* Only for your own week - it writes, and this screen only ever reads
                somebody else's. */}
            {!viewing && (
              <Button hint={"Book the same time on several days at once"} variant="outline" className="ml-2" onClick={() => setBulkOpen(true)}>
                <CalendarRange className="size-4" />
                Fill several days
              </Button>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ViewToggle view={view} onChange={(next) => setParam({ view: next })} />
          {canSeeOthers && (people?.length ?? 0) > 0 && (
            <div className="w-56">
              <Combobox
                value={employeeId}
                onChange={setEmployeeId}
                options={(people ?? [])
                  .filter((person) => person.id !== session.id)
                  .map((person) => ({
                    value: person.id,
                    label: person.displayName,
                    description: person.jobTitle ?? undefined,
                  }))}
                placeholder="My week"
                searchPlaceholder="Search people"
              />
            </div>
          )}
        </div>
        {week && (
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <p className="text-sm text-muted-foreground">
              {formatRange(week.weekStart, week.weekEnd)}
            </p>
            <p className="text-sm">
              Week total{' '}
              <span className="ts-num text-base font-semibold">
                {formatDuration(week.totalSeconds)}
              </span>
            </p>
          </div>
        )}
      </div>

      {active.isError && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm">
            {active.error instanceof Error ? active.error.message : 'Could not load this week.'}
          </AlertDescription>
        </Alert>
      )}

      {owedBefore.length > 0 && (
        <Alert className="border-amber-500/40">
          <AlertDescription className="text-sm leading-relaxed">
            Fill and submit your earlier {owedBefore.length === 1 ? 'week' : 'weeks'} first:{' '}
            {owedBefore.map((part, i) => (
              <React.Fragment key={part.start}>
                {i > 0 && ', '}
                <button type="button" className="font-medium underline underline-offset-2"
                  onClick={() => setAnchor(startOfWeek(part.start))}>
                  {formatRange(part.start, part.end)}
                </button>{' '}
                <span className="text-muted-foreground">
                  ({part.status === 'DENIED'
                    ? 'sent back'
                    : part.actualSeconds === 0
                      ? 'nothing recorded'
                      : `${formatDuration(part.actualSeconds)} of ${formatDuration(part.expectedSeconds)}, not submitted`})
                </span>
              </React.Fragment>
            ))}
            .
          </AlertDescription>
        </Alert>
      )}

      {submitError && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm leading-relaxed">{submitError}</AlertDescription>
        </Alert>
      )}

      {active.isPending && <Skeleton className="h-72 w-full" />}

      {week && !readOnly && (
        <WeekStatus
          periods={week.periods}
          busy={submit.isPending ? (submit.variables ?? null) : null}
          unsaved={view === 'grid' && gridDirty}
          onSubmit={async (periodStart) => {
            setSubmitError(null);
            try {
              await submit.mutateAsync(periodStart);
            } catch (err) {
              setSubmitError(
                err instanceof ApiError ? err.message : 'Could not submit that week.',
              );
            }
          }}
        />
      )}

      {/*
       * One line, shown once regardless of which view (grid or list) is
       * active - both are the same week, and duplicating the banner into each
       * view would mean it could drift out of sync with the panel's query
       * invalidation. Never shown for somebody else's week: /calendar/suggestions
       * is always the signed-in person's own calendar, and rendering it under
       * another person's name here would look like it were reading THEIRS.
       */}
      {week && !viewing && (
        <CalendarBanner week={week.weekStart} timeZone={session.timezone} />
      )}

      {view === 'grid' && gridQuery.data && <WeekGrid grid={gridQuery.data} onDirtyChange={setGridDirty} />}

      {view === 'list' && listQuery.data && (
        <div className="space-y-3">
          {listQuery.data.days.map((day) => (
            <DayRow
              key={day.date}
              day={day}
              readOnly={readOnly}
              onAdd={() => setEditing({ date: day.date })}
              onEdit={(entry) => setEditing({ date: day.date, entry })}
            />
          ))}
        </div>
      )}

      {week && (
        <BulkDialog
          open={bulkOpen}
          onOpenChange={setBulkOpen}
          defaultFrom={week.weekStart}
          defaultTo={week.weekEnd}
        />
      )}

      {editing && (
        <EntryDialog
          open
          onOpenChange={(open) => !open && setEditing(null)}
          workDate={editing.date}
          entry={editing.entry}
        />
      )}
    </PageContainer>
  );
}

/**
 * Grid or list.
 *
 * Two buttons rather than a dropdown: with exactly two options, a dropdown hides
 * one of them behind a click and says nothing about what the alternative is.
 */
function ViewToggle({ view, onChange }: { view: View; onChange: (next: View) => void }) {
  return (
    <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="How to edit this week">
      {(
        [
          { id: 'grid' as const, label: 'Grid', icon: LayoutGrid },
          { id: 'list' as const, label: 'List', icon: List },
        ]
      ).map(({ id, label, icon: Icon }) => (
        <Button
          key={id}
          hint={id === 'grid' ? 'Show the week as a grid' : 'Show every entry as a list'}
          size="sm"
          variant={view === id ? 'secondary' : 'ghost'}
          aria-pressed={view === id}
          className="gap-1.5"
          onClick={() => onChange(id)}
        >
          <Icon className="size-4" />
          {label}
        </Button>
      ))}
    </div>
  );
}

function DayRow({
  day,
  readOnly,
  onAdd,
  onEdit,
}: {
  day: WeekDay;
  /** Somebody else's week. Read-only counts as closed, exactly as it does in the
      grid - one flag, so the two surfaces cannot drift apart on who may write. */
  readOnly: boolean;
  onAdd: () => void;
  onEdit: (entry: WeekEntry) => void;
}) {
  const isToday = day.date === orgToday();
  const closed = day.locked || readOnly;
  const isEmpty = day.entries.length === 0;
  const remove = useDeleteEntry();

  return (
    <Card className={isToday ? 'border-primary' : undefined}>
      <CardContent className="p-0">
        <div className="flex items-baseline justify-between border-b px-4 py-2.5">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-semibold">{weekdayLabel(day.date)}</span>
            <span className="ts-num text-xs text-muted-foreground">{dayLabel(day.date)}</span>
            {isToday && <Badge variant="secondary">Today</Badge>}
            {day.locked && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Lock className="size-3" />
                    submitted
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  This week has gone for approval. Ask your supervisor to reopen it.
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`ts-num text-sm font-medium ${isEmpty ? 'text-muted-foreground' : ''}`}
            >
              {formatDuration(day.totalSeconds)}
            </span>
            {/* Absent rather than disabled on a locked day: a control that cannot do
                anything is noise, and the lock chip beside it already says why. */}
            {!closed && (
              <Button hint={"Add time on this day"} variant="ghost" size="sm" onClick={onAdd}>
                <Plus className="size-4" />
                Add
              </Button>
            )}
          </div>
        </div>

        {isEmpty ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">
            {closed ? 'Nothing recorded, and this day is closed.' : 'Nothing recorded.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-sm">
              <tbody>
                {day.entries.map((entry) => (
                  <tr key={entry.id} className="border-b last:border-b-0">
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{entry.activity.name}</div>
                      <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                        <span>
                          {entry.project.customer.name} · {entry.project.name}
                        </span>
                        <TagChips tags={entry.tags} />
                      </div>
                    </td>
                    {/* pre-wrap because 18 descriptions contain line breaks
                        somebody typed, and the default collapses them into one
                        line. The previous system had Markdown switched on for
                        this field, but not one of 1,414 descriptions uses any
                        Markdown - line breaks are the whole of what was meant. */}
                    <td className="whitespace-pre-wrap px-4 py-2.5 text-muted-foreground">
                      {entry.description ?? <span className="opacity-50">—</span>}
                    </td>
                    <td className="ts-num whitespace-nowrap px-4 py-2.5 text-right text-xs text-muted-foreground">
                      {clock(entry.begin)}–{entry.end ? clock(entry.end) : '…'}
                    </td>
                    <td className="ts-num whitespace-nowrap px-4 py-2.5 text-right font-medium">
                      {entry.durationSeconds === null
                        ? 'running'
                        : formatDuration(entry.durationSeconds)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2.5 text-right">
                      {!closed && (
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" aria-label="Edit entry"
                            onClick={() => onEdit(entry)}>
                            <Pencil className="size-4" />
                          </Button>
                          <ConfirmButton
                            variant="ghost"
                            size="icon"
                            aria-label="Delete entry"
                            disabled={remove.isPending}
                            pending={remove.isPending}
                            title="Delete this entry?"
                            description={
                              <>
                                {entry.project.name} — {entry.activity.name},{' '}
                                {entry.durationSeconds === null
                                  ? 'still running'
                                  : formatDuration(entry.durationSeconds)}
                                {entry.description ? ` — "${entry.description}"` : ''}. The recorded time is
                                removed and the day&rsquo;s total drops.
                              </>
                            }
                            confirmLabel="Delete entry"
                            destructive
                            onConfirm={() => remove.mutate(entry.id, { onError: toastFailure('Could not delete that entry') })}
                          >
                            <Trash2 className="size-4" />
                          </ConfirmButton>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const UTC = { timeZone: 'UTC' } as const;

function weekdayLabel(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-IN', { weekday: 'long', ...UTC });
}

function dayLabel(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    ...UTC,
  });
}

function formatRange(from: string, to: string): string {
  const f = (d: string) =>
    new Date(`${d}T00:00:00.000Z`).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      ...UTC,
    });
  return `${f(from)} – ${f(to)}`;
}

/** Entry times are instants, so these render in the viewer's zone, not UTC. */
function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
}
